
import { noChange } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume, createContext } from '@lit/context'
import { LitHauntedElement } from '../../mixins/lit-haunted-element';
import { useEffect } from 'haunted';
import { App, createApp } from 'vue'
import { evaluateESM } from '../../functions/synthetic_import';
import { isNullish } from '../../functions/nullish';

/*

<vue-template props="{ &quot;name&quot;: &quot;Bob&quot; }">
    <script type="module">
        export default {
            data() {
                return {
                    msg: 'Hello World',
                    count: 0,
                }
            }
        }
    </script>
    <template>
        <p>A prop: {{ name }}</p>
        <div class="example">
            {{ msg }}
        </div>
        <button @click.prevent="count++">{{ count }}</button>
    </template>
    <style scoped>
        .example {
            color: red;
        }
    </style>
</vue-template>

*/

export const vueTemplateContext = createContext<unknown>(Symbol('vue-template-context'));

@customElement('vue-template')
export class VueTemplate extends LitHauntedElement {

  @consume({ context: vueTemplateContext, subscribe: true })
  @state()
  _contextProps: any | undefined;

  @property({ type: Object, attribute: 'props' })
  props: any | undefined;

  #instanceNumber: number = Math.floor(Math.random() * 100) + 1;
  #lock = false;

  liveRootComponent: unknown = undefined;

  app?: App;

  #getProps(): any | undefined {
    return this.props ?? this._contextProps;
  }

  async renderVue() {
    const template = this.querySelector('template');
    const script = this.querySelector('script[type=module]');
    if (template === null) return;
    if (script === null || script.textContent === null) return;

    const root = document.createElement('div');
    root.setAttribute('vue-template-root', '');
    this.append(root);
    const importedDocument = document.importNode(template.content, true);
    root.append(...importedDocument.childNodes);

    const module = await evaluateESM(script.textContent);
    let config = {};
    // If there is a "default" export in the ES module, then make it part of our config
    if ('default' in module && typeof module.default === 'object') {
      config = {
        ...config,
        ...module.default,
      }
    }

    const props = this.#getProps();

    // Unmount if the app already exists
    this.app?.unmount();

    this.app = createApp({
      props: [...Object.keys(props)],
      ...config,
    }, {...props});

    this.app?.mount(root);
  }

  protected override createRenderRoot() {
    return this;
  }

  render() {
    useEffect(async () => {

      const props = this.#getProps();
      if (isNullish(props)) return;
      if (this.#lock) return;
      //if (!isNullish(this.app)) return;
      console.debug(`[vue-template#${this.#instanceNumber}] Rendering Vue with props:`, props);
      this.#lock = true;
      try {
        await this.renderVue();
      }
      finally {
        this.#lock = false;
      }

    }, [this._contextProps, this.props]);
    return noChange;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vue-template': VueTemplate
  }
}
